import React, { useEffect, useState } from 'react';
import tournamentService from '../services/tournamentService';
import { toast } from 'sonner';
import { PiTrophy } from 'react-icons/pi';
import { FiEdit3 } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import '../styles/AdminDashboard.css';

const emptyForm = {
    name: '',
    sportType: 'FOOTBALL',
    year: '',
    sportDbApiTournamentId: '',
    apiFootballId: '',
    apiFootballIdV3: '',
    apiFootballTeamId: '',
    international: false,
    activeForImport: false,
    status: '',
    startDate: '',
    endDate: '',
    overtimeEnabled: false,
    twoLegKnockout: false,
};

const UpdateTournament = () => {
    const { t } = useTranslation();
    const [tournaments, setTournaments] = useState([]);
    const [selectedId, setSelectedId] = useState('');
    const [loadingList, setLoadingList] = useState(true);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [originalStatus, setOriginalStatus] = useState('');

    useEffect(() => {
        const fetchTournaments = async () => {
            setLoadingList(true);
            try {
                const data = await tournamentService.getAllTournaments();
                const sorted = (Array.isArray(data) ? data : [])
                    .slice()
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                setTournaments(sorted);
            } catch (e) {
                console.error('Failed to load tournaments', e);
                toast.error('Nepodařilo se načíst turnaje');
            } finally {
                setLoadingList(false);
            }
        };
        fetchTournaments();
    }, []);

    useEffect(() => {
        if (!selectedId) {
            setForm(emptyForm);
            setOriginalStatus('');
            return;
        }
        const fetchDetail = async () => {
            setLoadingDetail(true);
            try {
                const t = await tournamentService.getTournamentById(selectedId);
                setForm({
                    name: t.name || '',
                    sportType: t.sportType || 'FOOTBALL',
                    year: t.year || '',
                    sportDbApiTournamentId: t.sportDbApiTournamentId ?? '',
                    apiFootballId: t.apiFootballId ?? '',
                    apiFootballIdV3: t.apiFootballIdV3 ?? '',
                    apiFootballTeamId: t.apiFootballTeamId ?? '',
                    international: !!t.international,
                    activeForImport: !!t.activeForImport,
                    status: t.status || '',
                    startDate: t.startDate || '',
                    endDate: t.endDate || '',
                    overtimeEnabled: !!t.overtimeEnabled,
                    twoLegKnockout: !!t.twoLegKnockout,
                });
                setOriginalStatus(t.status || '');
            } catch (e) {
                console.error('Failed to load tournament detail', e);
                toast.error('Nepodařilo se načíst detail turnaje');
            } finally {
                setLoadingDetail(false);
            }
        };
        fetchDetail();
    }, [selectedId]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedId) return;

        const statusChanged = (form.status || '') !== (originalStatus || '');
        if (statusChanged && form.status) {
            const ok = window.confirm(
                `Změna stavu turnaje na "${form.status}" se automaticky propíše i do všech lig tohoto turnaje. Pokračovat?`
            );
            if (!ok) return;
        }

        setSubmitting(true);
        try {
            const payload = {
                name: form.name.trim(),
                sportType: form.sportType,
                year: form.year.trim() || null,
                sportDbApiTournamentId: form.sportDbApiTournamentId !== '' ? Number(form.sportDbApiTournamentId) : null,
                apiFootballId: form.apiFootballId !== '' ? Number(form.apiFootballId) : null,
                apiFootballIdV3: form.apiFootballIdV3 !== '' ? Number(form.apiFootballIdV3) : null,
                apiFootballTeamId: form.apiFootballTeamId !== '' ? Number(form.apiFootballTeamId) : null,
                international: form.international,
                activeForImport: form.activeForImport,
                status: form.status || null,
                startDate: form.startDate || null,
                endDate: form.endDate || null,
                overtimeEnabled: form.overtimeEnabled,
                twoLegKnockout: form.twoLegKnockout && form.overtimeEnabled,
            };
            await tournamentService.updateTournament(selectedId, payload);
            toast.success(
                statusChanged && form.status
                    ? `Turnaj "${payload.name}" aktualizován a stav lig nastaven na ${form.status}`
                    : `Turnaj "${payload.name}" byl úspěšně aktualizován`
            );
            setOriginalStatus(form.status || '');
            setTournaments(prev => prev.map(t => (
                String(t.id) === String(selectedId)
                    ? { ...t, name: payload.name, year: payload.year, sportType: payload.sportType }
                    : t
            )));
        } catch (error) {
            const detail = error?.response?.data?.message || error?.response?.data || error?.message || 'Neznámá chyba';
            toast.error(`Nepodařilo se aktualizovat turnaj: ${detail}`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="admin-dashboard-container">
            <h2 className="admin-section-title">Úprava turnaje</h2>
            <form onSubmit={handleSubmit} className="admin-action-card" style={{ maxWidth: 640 }}>
                <div className="admin-action-header">
                    <span className="admin-action-icon"><FiEdit3 /></span>
                    <h3>Aktualizovat turnaj</h3>
                </div>

                <p className="admin-action-desc">
                    Vyber existující turnaj, jeho údaje se načtou z backendu. Při změně stavu turnaje se automaticky aktualizuje i stav všech lig v tomto turnaji.
                </p>

                <div className="admin-action-body">
                    <div className="admin-field">
                        <label htmlFor="ut-select">Turnaj *</label>
                        <select
                            id="ut-select"
                            className="admin-select"
                            value={selectedId}
                            onChange={(e) => setSelectedId(e.target.value)}
                            disabled={submitting || loadingList}
                            required
                        >
                            {loadingList ? (
                                <option value="">Načítám turnaje…</option>
                            ) : (
                                <>
                                    <option value="">— Vyberte turnaj —</option>
                                    {tournaments.map(tournament => (
                                        <option key={tournament.id} value={tournament.id}>
                                            {t(`tournaments.${tournament.name}`, { year: tournament.year })}
                                        </option>
                                    ))}
                                </>
                            )}
                        </select>
                    </div>

                    {loadingDetail && (
                        <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Načítám detail turnaje…</p>
                    )}

                    {selectedId && !loadingDetail && (
                        <>
                            <div className="admin-field">
                                <label htmlFor="ut-name">Název *</label>
                                <input
                                    id="ut-name"
                                    className="admin-input"
                                    type="text"
                                    name="name"
                                    value={form.name}
                                    onChange={handleChange}
                                    maxLength={150}
                                    required
                                    disabled={submitting}
                                />
                            </div>

                            <div className="admin-field">
                                <label htmlFor="ut-sport">Sport *</label>
                                <select
                                    id="ut-sport"
                                    className="admin-select"
                                    name="sportType"
                                    value={form.sportType}
                                    onChange={handleChange}
                                    required
                                    disabled={submitting}
                                >
                                    <option value="FOOTBALL">Fotbal</option>
                                    <option value="HOCKEY">Hokej</option>
                                </select>
                            </div>

                            <div className="admin-field">
                                <label htmlFor="ut-status">Stav turnaje</label>
                                <select
                                    id="ut-status"
                                    className="admin-select"
                                    name="status"
                                    value={form.status}
                                    onChange={handleChange}
                                    disabled={submitting}
                                >
                                    <option value="">— Nenastaveno —</option>
                                    <option value="NOT_STARTED">NOT_STARTED</option>
                                    <option value="IN_PROGRESS">IN_PROGRESS</option>
                                    <option value="FINISHED">FINISHED</option>
                                </select>
                                <small style={{ color: '#6b7280' }}>
                                    Změna stavu se propíše i do všech lig v tomto turnaji.
                                </small>
                            </div>

                            <div className="admin-field">
                                <label htmlFor="ut-startDate">Začátek turnaje</label>
                                <input
                                    id="ut-startDate"
                                    className="admin-input"
                                    type="date"
                                    name="startDate"
                                    value={form.startDate}
                                    onChange={handleChange}
                                    disabled={submitting}
                                />
                            </div>

                            <div className="admin-field">
                                <label htmlFor="ut-endDate">Konec turnaje</label>
                                <input
                                    id="ut-endDate"
                                    className="admin-input"
                                    type="date"
                                    name="endDate"
                                    value={form.endDate}
                                    onChange={handleChange}
                                    disabled={submitting}
                                />
                            </div>

                            <div className="admin-field">
                                <label htmlFor="ut-year">Rok / sezona</label>
                                <input
                                    id="ut-year"
                                    className="admin-input"
                                    type="text"
                                    name="year"
                                    value={form.year}
                                    onChange={handleChange}
                                    placeholder="např. 2024/2025"
                                    maxLength={20}
                                    disabled={submitting}
                                />
                            </div>

                            <div className="admin-field">
                                <label htmlFor="ut-apiFootballId">API-Football ID (v2)</label>
                                <input
                                    id="ut-apiFootballId"
                                    className="admin-input"
                                    type="number"
                                    name="apiFootballId"
                                    value={form.apiFootballId}
                                    onChange={handleChange}
                                    min="0"
                                    disabled={submitting}
                                />
                            </div>

                            <div className="admin-field">
                                <label htmlFor="ut-apiFootballIdV3">API-Football ID (v3)</label>
                                <input
                                    id="ut-apiFootballIdV3"
                                    className="admin-input"
                                    type="number"
                                    name="apiFootballIdV3"
                                    value={form.apiFootballIdV3}
                                    onChange={handleChange}
                                    min="0"
                                    disabled={submitting || form.apiFootballTeamId !== ''}
                                />
                            </div>

                            <div className="admin-field">
                                <label htmlFor="ut-apiFootballTeamId">API-Football ID týmu (týmový turnaj)</label>
                                <input
                                    id="ut-apiFootballTeamId"
                                    className="admin-input"
                                    type="number"
                                    name="apiFootballTeamId"
                                    value={form.apiFootballTeamId}
                                    onChange={handleChange}
                                    placeholder="např. 604 (Sparta Praha)"
                                    min="0"
                                    disabled={submitting || form.apiFootballIdV3 !== ''}
                                />
                                <small style={{ color: '#6b7280' }}>
                                    Týmový turnaj = všechny zápasy jednoho týmu v sezóně (liga, pohár, Evropa, přáteláky).
                                    Jen pro fotbal; vyplňte buď ID soutěže (v3), nebo ID týmu — ne obojí.
                                    Prodloužení se určuje automaticky podle soutěže a kola, checkboxy níže se ignorují.
                                    Pro import je nutný rok/sezona a konec turnaje.
                                </small>
                            </div>

                            <div className="admin-field">
                                <label htmlFor="ut-sportDbId">TheSportsDB API ID</label>
                                <input
                                    id="ut-sportDbId"
                                    className="admin-input"
                                    type="number"
                                    name="sportDbApiTournamentId"
                                    value={form.sportDbApiTournamentId}
                                    onChange={handleChange}
                                    min="0"
                                    disabled={submitting}
                                />
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 4 }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem', color: '#374151' }}>
                                    <input
                                        type="checkbox"
                                        name="international"
                                        checked={form.international}
                                        onChange={handleChange}
                                        disabled={submitting}
                                        style={{ width: 16, height: 16, cursor: 'pointer' }}
                                    />
                                    Mezinárodní turnaj
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem', color: '#374151' }}>
                                    <input
                                        type="checkbox"
                                        name="activeForImport"
                                        checked={form.activeForImport}
                                        onChange={handleChange}
                                        disabled={submitting}
                                        style={{ width: 16, height: 16, cursor: 'pointer' }}
                                    />
                                    Aktivní pro import zápasů (cron job)
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem', color: '#374151' }}>
                                    <input
                                        type="checkbox"
                                        name="overtimeEnabled"
                                        checked={form.overtimeEnabled}
                                        onChange={handleChange}
                                        disabled={submitting}
                                        style={{ width: 16, height: 16, cursor: 'pointer' }}
                                    />
                                    Prodloužení povoleno (overtime enabled)
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: form.overtimeEnabled ? 'pointer' : 'not-allowed', fontSize: '0.9rem', color: form.overtimeEnabled ? '#374151' : '#9ca3af' }}>
                                    <input
                                        type="checkbox"
                                        name="twoLegKnockout"
                                        checked={form.twoLegKnockout}
                                        onChange={handleChange}
                                        disabled={submitting || !form.overtimeEnabled}
                                        style={{ width: 16, height: 16, cursor: form.overtimeEnabled ? 'pointer' : 'not-allowed' }}
                                    />
                                    Vyřazovací fáze na dva zápasy (two-leg knockout)
                                </label>
                            </div>
                        </>
                    )}
                </div>

                <button
                    type="submit"
                    className="admin-action-btn"
                    disabled={submitting || !selectedId || loadingDetail || !form.name.trim()}
                >
                    {submitting ? 'Ukládám…' : <><PiTrophy style={{ marginRight: 6 }} /> Uložit změny</>}
                </button>
            </form>
        </div>
    );
};

export default UpdateTournament;
